import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { apiClient } from '../lib/api';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const tokenParam = searchParams.get('token');
      if (!tokenParam) {
        setError(t('auth.resetPassword.invalidToken'));
        setTokenInvalid(true);
        return;
      }

      setToken(tokenParam);

      // Validate token with backend
      try {
        const response = await apiClient.get(
          `/auth/password/reset/validate?token=${encodeURIComponent(tokenParam)}`
        );

        if (!response.data.valid) {
          setError(t('auth.resetPassword.invalidToken'));
          setTokenInvalid(true);
        }
      } catch (err) {
        setError(t('auth.resetPassword.invalidToken'));
        setTokenInvalid(true);
      }
    };

    validateToken();
  }, [searchParams, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.resetPassword.passwordTooShort'));
      return;
    }

    // Validate password strength (match backend requirements)
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError('Password must contain at least one special character');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('/auth/password/reset', {
        token,
        newPassword: password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t('auth.resetPassword.error')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          {!tokenInvalid && (
            <>
              <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">
                {t('auth.resetPassword.title')}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t('auth.resetPassword.subtitle')}
              </p>
            </>
          )}
        </div>

        {success ? (
          <div className="space-y-6">
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="font-semibold text-green-900 dark:text-green-200 mb-1">
                    {t('auth.resetPassword.successTitle')}
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {t('auth.resetPassword.successMessage')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {tokenInvalid ? (
              <div className="space-y-6">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                  <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => window.location.href = '/forgot-password'}
                >
                  {t('auth.resetPassword.requestNew')}
                </Button>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    type="password"
                    label={t('auth.resetPassword.newPassword')}
                    placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    autoFocus
                  />

                  <Input
                    type="password"
                    label={t('auth.resetPassword.confirmPassword')}
                    placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />

                  {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                      <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isLoading}
                    className="w-full"
                  >
                    {t('auth.resetPassword.submit')}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    <a
                      href="/login"
                      className="font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
                    >
                      {t('auth.resetPassword.backToLogin')}
                    </a>
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
